import React, { useState, useLayoutEffect, useRef } from 'react';
import { Tab } from '../Tabs/components';
import { hasFieldError } from './tabErrors';
import { keyCode } from '../../helpers';

import './FormTabs.css';

/**
 * The tab strip a side-dialog form is split into.
 *
 * Each child is a `<div label="...">` whose children are the tab's content; only the active one is
 * mounted. A form leaves out the tabs its current state has no use for, and one of those going is
 * never left as the selection -- the first tab takes over. A tab whose fields the server rejected
 * is marked, so an error on a tab out of sight is still findable.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - One element per tab, each carrying a `label`.
 * @param {{ tab: string }} [props.defaultSelectedTab] - The tab to open, by lowercased label.
 * @param {boolean} [props.hideTabsIfOnlyOne] - Hides the strip when a single tab is left.
 * @param {Record<string, string>} [props.formErrors] - Field-keyed errors from a 400 response.
 * @returns {JSX.Element}
 */
export const FormTabs = ({
  children,
  defaultSelectedTab,
  hideTabsIfOnlyOne = false,
  formErrors = {},
}) => {
  const [activeTab, setActiveTab] = useState(defaultSelectedTab?.tab);
  const refTabs = useRef([]);

  // The tabs there really are. A tab a form has no use for in its current state is written as a
  // condition, so it arrives here as false -- and a form with a single tab does not arrive as a
  // list at all.
  const tabs = (Array.isArray(children) ? children : [children]).filter(
    child => child?.props?.label
  );

  // The tab in front of the user: the one selected for as long as it is there, and the first one
  // once it is not. Both the strip and the body find their tab by matching this, so without the
  // fallback, dropping the tab being looked at -- an Attribute that stops being a Dimension loses
  // its Values tab -- would leave nothing marked and nothing shown. The selection itself is left
  // alone, so a tab that comes back is the one returned to.
  const currentTab = tabs.some(tab => tab.props.label.toLowerCase() === activeTab)
    ? activeTab
    : tabs[0]?.props.label.toLowerCase();

  useLayoutEffect(() => {
    setActiveTab((defaultSelectedTab?.tab || tabs[0]?.props.label)?.toLowerCase());
  }, [defaultSelectedTab]);

  const onClickTabItem = tab => {
    setActiveTab(tab);
  };

  const focusNextListItem = direction => {
    const activeElement = document.activeElement;

    const activeElementIndex = refTabs.current.findIndex(el => el === activeElement);
    const activeTabIndex =
      activeElementIndex < 0
        ? tabs.findIndex(tab => tab.props.label.toLowerCase() === currentTab)
        : activeElementIndex;

    // Counted over the tabs rendered, and reached for only if it is still there: a tab that has
    // gone leaves its button behind in the list of refs until the next render replaces it.
    if (activeTabIndex >= 0) {
      if (direction === keyCode.RIGHT_ARROW && activeTabIndex + 1 < tabs.length) {
        refTabs.current[activeTabIndex + 1]?.focus();
      } else if (direction === keyCode.LEFT_ARROW && activeTabIndex > 0) {
        refTabs.current[activeTabIndex - 1]?.focus();
      }
    } else {
      refTabs.current[0]?.focus();
    }
  };

  const handleKeyDown = (e, val) => {
    switch (e.keyCode) {
      case keyCode.LEFT_ARROW:
        focusNextListItem(keyCode.LEFT_ARROW);
        break;

      case keyCode.RIGHT_ARROW:
        focusNextListItem(keyCode.RIGHT_ARROW);
        break;

      default:
        break;
    }
  };

  //if (hideTabsIfOnlyOne && children.filter(x => x).length <= 1) {
  //  return children;
  //}

  return (
    <div className="FormTabs">
      {!(hideTabsIfOnlyOne && tabs.length <= 1) && (
        <div className="tab-list" role="tablist" onKeyDown={handleKeyDown}>
          {tabs.map((child, i) => {
            const { label, resultCount, disabled, tooltip } = child.props;

            // The page-level tab button, which these tabs differ from only in how they are styled.
            // A form tab has no id of its own -- its label is what identifies it -- so the lowercased
            // label is what is handed to the button as its tabid.
            return (
              <Tab
                ref={el => (refTabs.current[i] = el)}
                activeTab={currentTab}
                key={label}
                label={label}
                tabid={label.toLowerCase()}
                resultCount={resultCount}
                disableTab={disabled}
                tooltip={tooltip}
                // A rejected field is on whichever tab declares it, which is not necessarily the one
                // being looked at. Marking the tab is what keeps a save from failing for a reason
                // the user cannot see.
                error={hasFieldError(child.props.children, formErrors)}
                onClick={onClickTabItem}
              />
            );
          })}
        </div>
      )}
      <div className="tab-content" role="tabpanel">
        {tabs.map(child =>
          child.props.label.toLowerCase() === currentTab ? child.props.children : undefined
        )}
      </div>
    </div>
  );
};
